import React, {useEffect, useState} from 'react';
import {doc, getDoc, onSnapshot} from 'firebase/firestore';
import {db} from '../../Security/firebase-config';
import {IMPROVEMENT_NOTE_MAPS} from '../../utils/teamPlanning';
import {classificationLabel, noteClassification} from '../../utils/noteClassification';
import {TEAM_ROLE_ORDER,teamRoleLabel,teamRoleColor} from '../../utils/teamRoleDisplay';
import './NoteClassification.css';

export function AssigneeDisplay({value,members=[]}) {
  const c=noteClassification(value);
  if(!c.assigneeUids.length)return <>{c.assignees.join(', ') || 'Team'}</>;
  return <>{c.assigneeUids.map((uid,index)=>{
    const person=members.find(member=>member.uid===uid);
    return <React.Fragment key={uid}>{index>0?', ':''}<span className="ImprovementPointAssignee" style={{color:teamRoleColor(person?.role || 'Player')}}>{person?.username || c.assignees[index] || uid}</span></React.Fragment>;
  })}</>;
}

export function ClassificationDisplay({value, defaults, showScope = true, members=[], actions}) {
  const c = noteClassification(value, defaults);
  return <div className={`ImprovementPointHeader${showScope && c.assigneeUids.length>=3 ? " ImprovementPointHeader--many-assignees" : ""}`} aria-label={classificationLabel(value, defaults)}>
    {showScope && <span>{c.scope === 'map' ? c.mapName || c.mapId || 'Map Specific' : 'General'}</span>}
    <span>{c.side}</span><span><AssigneeDisplay value={c} members={members}/></span>{actions && <div className="ImprovementPointHeader__actions">{actions}</div>}
  </div>;
}

export function useClassificationRoster(teamId,members) {
  const [directory, setDirectory] = useState([]);
  useEffect(() => {
    if (members || !teamId) return undefined;
    let active = true;
    let revision = 0;
    setDirectory([]);
    const stop = onSnapshot(doc(db, 'teams', teamId), async snapshot => {
      const current = ++revision;
      const team = snapshot.data() || {};
      const people = await Promise.all((Array.isArray(team.members) ? team.members : []).map(async member => {
        const uid = typeof member === 'string' ? member : member.uid;
        if (!uid) return null;
        const publicUser = await getDoc(doc(db, 'uids', uid)).catch(() => null);
        return {uid, username: publicUser?.data()?.username || team.memberUsernames?.[uid] || member?.username || uid,
          role:team.teamRoles?.[uid] || team.memberRoles?.[uid] || team.roles?.[uid] || member?.role || 'Player'};
      }));
      if (active && current === revision) setDirectory(people.filter(Boolean));
    }, () => {if (active) setDirectory([]);});
    return () => {active = false; stop();};
  }, [teamId, members]);
  return [...(members || directory)].sort((a,b)=>TEAM_ROLE_ORDER.indexOf(teamRoleLabel(a.role || 'Player'))-TEAM_ROLE_ORDER.indexOf(teamRoleLabel(b.role || 'Player')) || a.username.localeCompare(b.username));
}

export default function NoteClassification({value, onChange, teamId, members, defaults = {}, disabled = false, showScope = true, fixedMap = false, activeField}) {
  const roster=useClassificationRoster(teamId,members);
  const c = noteClassification(value, defaults);
  const patch = change => onChange({...value, ...c, ...change});
  return <div className="NoteClassification">
    {showScope && (!activeField || activeField==='scope') && <><label>Scope<select disabled={disabled} value={c.scope} onChange={e => patch({scope:e.target.value, mapId:e.target.value === 'map' ? defaults.mapId || null : null, mapName:e.target.value === 'map' ? defaults.mapName || (fixedMap ? defaults.mapId || '' : IMPROVEMENT_NOTE_MAPS[0]) : ''})}><option value="general">General</option><option value="map">Map Specific</option></select></label>
      {c.scope === 'map' && !fixedMap && <label>Map<select disabled={disabled} value={c.mapName || c.mapId || ''} onChange={e => patch({mapName:e.target.value,mapId:e.target.value === defaults.mapName ? defaults.mapId || null : null})}><option value="">Choose a map</option>{[...new Set([...IMPROVEMENT_NOTE_MAPS, c.mapName || c.mapId, defaults.mapName].filter(Boolean))].map(name => <option key={name}>{name}</option>)}</select></label>}</>}
    {(!activeField || activeField==='assignee') && <fieldset disabled={disabled}><legend>Assigned to</legend><label><input type="checkbox" checked={!c.assigneeUids.length} onChange={()=>patch({assigneeUids:[],assignees:[],assigneeUid:null,assignee:null})}/>Team</label>
      {[...roster.filter(p=>p.uid),...c.assigneeUids.filter(uid=>!roster.some(p=>p.uid===uid)).map((uid,index)=>({uid,username:c.assignees[index]||uid}))].map(p=><label key={p.uid}><input type="checkbox" checked={c.assigneeUids.includes(p.uid)} onChange={e=>{
        const ids=e.target.checked?[...c.assigneeUids,p.uid]:c.assigneeUids.filter(uid=>uid!==p.uid);
        const names=ids.map(uid=>roster.find(member=>member.uid===uid)?.username||c.assignees[c.assigneeUids.indexOf(uid)]||uid);
        patch({assigneeUids:ids,assignees:names,assigneeUid:ids[0]||null,assignee:names[0]||null});
      }}/><span style={{color:teamRoleColor(p.role || 'Player')}}>{p.username}</span></label>)}
    </fieldset>}
    {(!activeField || activeField==='side') && <label>Side<select disabled={disabled} value={c.side} onChange={e => patch({side:e.target.value})}>{['DEF','ATK','ATK & DEF'].map(side=><option key={side}>{side}</option>)}</select></label>}
  </div>;
}
